/**
 * Le catalogue des prestations, avec leur prix.
 *
 * Chaque variante de tarif est une prestation à part entière (« Ongle simple
 * mains », « Ongle simple mains et pieds », « Ongle avec poudre ») : pas de
 * système d'options, on ajoute une ligne au catalogue.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Chip, Switch, FormControlLabel, CircularProgress,
  Alert, Tooltip, Card, CardContent, InputAdornment,
} from '@mui/material';
import {
  Edit, Delete, Add, Search, Visibility, VisibilityOff, ContentCut,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import {
  prestationAPI, categoriePrestationAPI, extraireListe,
} from '../../../services/studioAPI';

const FORMULAIRE_VIDE = { nom: '', categorie: '', prix: '', description: '', est_active: true };

const htg = (valeur) => `${Number(valeur || 0).toLocaleString()} HTG`;

const Prestations = () => {
  const [prestations, setPrestations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [dialogOuvert, setDialogOuvert] = useState(false);
  const [enEdition, setEnEdition] = useState(false);
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);

  const charger = async () => {
    try {
      setLoading(true);
      setErreur(null);
      const [reponsePrestations, reponseCategories] = await Promise.all([
        prestationAPI.getAll(),
        categoriePrestationAPI.getAll(),
      ]);
      setPrestations(extraireListe(reponsePrestations));
      setCategories(extraireListe(reponseCategories));
    } catch (error) {
      const message = error.response?.data?.detail || error.message
        || 'Erreur lors du chargement du catalogue';
      setErreur(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const affichees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return prestations.filter((prestation) => {
      const correspond = !terme
        || `${prestation.nom} ${prestation.categorie_nom || ''}`.toLowerCase().includes(terme);
      const dansCategorie = !filtreCategorie || prestation.categorie === Number(filtreCategorie);
      return correspond && dansCategorie;
    });
  }, [prestations, recherche, filtreCategorie]);

  const prixMoyen = useMemo(() => {
    const actives = prestations.filter((p) => p.est_active);
    if (actives.length === 0) return 0;
    const total = actives.reduce((somme, p) => somme + Number(p.prix || 0), 0);
    return Math.round(total / actives.length);
  }, [prestations]);

  const ouvrirDialog = (prestation = null) => {
    if (prestation) {
      setFormulaire({
        id: prestation.id,
        nom: prestation.nom,
        categorie: prestation.categorie,
        prix: prestation.prix,
        description: prestation.description || '',
        est_active: prestation.est_active,
      });
      setEnEdition(true);
    } else {
      setFormulaire({ ...FORMULAIRE_VIDE, categorie: categories[0]?.id || '' });
      setEnEdition(false);
    }
    setDialogOuvert(true);
  };

  const enregistrer = async () => {
    if (!formulaire.nom.trim()) return toast.error('Le nom est obligatoire');
    if (!formulaire.categorie) return toast.error('Choisissez une section');
    if (formulaire.prix === '' || Number(formulaire.prix) < 0) {
      return toast.error('Saisissez un prix valide');
    }

    try {
      const donnees = { ...formulaire, prix: Number(formulaire.prix) };
      if (enEdition) {
        await prestationAPI.update(formulaire.id, donnees);
        toast.success('Prestation modifiée');
      } else {
        await prestationAPI.create(donnees);
        toast.success('Prestation créée');
      }
      setDialogOuvert(false);
      charger();
    } catch (error) {
      toast.error(
        error.response?.data?.nom?.[0] || error.response?.data?.prix?.[0]
        || error.response?.data?.detail || "Erreur lors de l'enregistrement"
      );
    }
  };

  const basculerStatut = async (prestation) => {
    try {
      await prestationAPI.patch(prestation.id, { est_active: !prestation.est_active });
      toast.success(prestation.est_active ? 'Retirée de la carte' : 'Remise sur la carte');
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  const supprimer = async (prestation) => {
    if (!window.confirm(`Supprimer « ${prestation.nom} » ?`)) return;
    try {
      await prestationAPI.delete(prestation.id);
      toast.success('Prestation supprimée');
      charger();
    } catch (error) {
      // PROTECT : une prestation déjà vendue reste liée à ses tickets
      toast.error(
        'Impossible de supprimer une prestation déjà vendue — désactivez-la plutôt'
      );
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Prestations</Typography>
          <Typography variant="body2" color="text.secondary">
            Le catalogue proposé à la caisse
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => ouvrirDialog()}
          disabled={categories.length === 0}
        >
          Nouvelle prestation
        </Button>
      </Box>

      {categories.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Créez d'abord une section (Ongles, Coiffure...) : une prestation doit y être rattachée.
        </Alert>
      )}
      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      {/* KPI */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Au catalogue</Typography>
            <Typography variant="h4" fontWeight="bold">{prestations.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Sur la carte</Typography>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {prestations.filter((p) => p.est_active).length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Prix moyen</Typography>
            <Typography variant="h4" fontWeight="bold">{htg(prixMoyen)}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filtres */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Chercher une prestation"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          sx={{ flex: '1 1 260px' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
          }}
        />
        <TextField
          size="small"
          select
          label="Section"
          value={filtreCategorie}
          onChange={(e) => setFiltreCategorie(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Toutes</MenuItem>
          {categories.map((categorie) => (
            <MenuItem key={categorie.id} value={categorie.id}>{categorie.nom}</MenuItem>
          ))}
        </TextField>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Prestation</TableCell>
              <TableCell>Section</TableCell>
              <TableCell align="right">Prix</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {affichees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <ContentCut sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography color="text.secondary" mt={1}>
                    {prestations.length === 0
                      ? 'Aucune prestation au catalogue'
                      : 'Aucune prestation ne correspond'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : affichees.map((prestation) => (
              <TableRow key={prestation.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{prestation.nom}</Typography>
                  {prestation.description && (
                    <Typography variant="caption" color="text.secondary">
                      {prestation.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={prestation.categorie_nom || '—'} variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>{htg(prestation.prix)}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={prestation.est_active ? 'Sur la carte' : 'Retirée'}
                    color={prestation.est_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={prestation.est_active ? 'Retirer de la carte' : 'Remettre'}>
                    <IconButton size="small" onClick={() => basculerStatut(prestation)}>
                      {prestation.est_active ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => ouvrirDialog(prestation)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton size="small" color="error" onClick={() => supprimer(prestation)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOuvert} onClose={() => setDialogOuvert(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{enEdition ? 'Modifier la prestation' : 'Nouvelle prestation'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nom"
              value={formulaire.nom}
              onChange={(e) => setFormulaire({ ...formulaire, nom: e.target.value })}
              placeholder="Ongle avec poudre"
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Section"
              select
              value={formulaire.categorie}
              onChange={(e) => setFormulaire({ ...formulaire, categorie: e.target.value })}
              fullWidth
              required
            >
              {categories.map((categorie) => (
                <MenuItem key={categorie.id} value={categorie.id}>{categorie.nom}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Prix"
              type="number"
              value={formulaire.prix}
              onChange={(e) => setFormulaire({ ...formulaire, prix: e.target.value })}
              fullWidth
              required
              InputProps={{ endAdornment: <InputAdornment position="end">HTG</InputAdornment> }}
              helperText="Le prix est figé sur le ticket au moment de la vente : le modifier ici ne touche pas les ventes passées."
            />
            <TextField
              label="Description"
              value={formulaire.description}
              onChange={(e) => setFormulaire({ ...formulaire, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formulaire.est_active}
                  onChange={(e) => setFormulaire({ ...formulaire, est_active: e.target.checked })}
                />
              }
              label="Proposée à la caisse"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOuvert(false)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrer}>
            {enEdition ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Prestations;
